import type { Card, List } from "@kandoo/shared";

/**
 * Compute a position value for inserting an item at `targetIndex` within a
 * list of existing items sorted ascending by `position`. Uses fractional
 * midpoints so only the moved item needs to be updated (one API call).
 *
 * `excludeId` removes an item (the one being moved) before computing.
 */
export function positionForIndex<T extends { id: string; position: number }>(
  items: T[],
  targetIndex: number,
  excludeId?: string,
): number {
  const sorted = items
    .filter((i) => i.id !== excludeId)
    .slice()
    .sort((a, b) => a.position - b.position);

  if (sorted.length === 0) return 0;
  if (targetIndex <= 0) return sorted[0].position - 1;
  if (targetIndex >= sorted.length) return sorted[sorted.length - 1].position + 1;

  const prev = sorted[targetIndex - 1];
  const next = sorted[targetIndex];
  return (prev.position + next.position) / 2;
}

/** Sorted copy of cards by position (preserves shape). */
export function sortCards<T extends Card>(cards: T[]): T[] {
  return cards.slice().sort((a, b) => a.position - b.position);
}

/** Sorted copy of lists by position (preserves extra fields like cards). */
export function sortLists<T extends List>(lists: T[]): T[] {
  return lists.slice().sort((a, b) => a.position - b.position);
}